/**
 * Resumen del chequeo previo (paso Confirmar): a cuántos les llega, quiénes quedan afuera y por
 * qué, qué bloquea el envío y qué conviene mirar.
 *
 * Los números los calcula el backend con la misma función que el envío (dry-run). Acá sólo se
 * ordenan (ORDEN_GATES) y se traducen. Nada se suma ni se resta en el front: si el total no
 * cierra, se muestra lo que dijo el backend, no una cuenta nuestra que lo tape.
 */

import { Link } from "react-router-dom";
import { AlertTriangle, Clock, OctagonAlert, Users } from "lucide-react";
import type { CampoDisponible, Chequeo, CodigoBloqueo, MapeoVariables } from "../types";
import type { PasoAsistente } from "../logic/chequeo";
import { filasExclusion } from "../logic/chequeo";
import { textoCalientes } from "../logic/calentamiento";
import { sinDecision } from "../logic/mapeo";
import { BLOQUEO, tituloBloqueo } from "../logic/textos";
import { num, plural } from "../../campana-nueva/utils/format";

interface Props {
  chequeo: Chequeo;
  /** La regla de fatiga de la marca: días y (opcional, 1 por defecto) cuántos mensajes como mucho, oficial + QR. */
  fatigaDias: number;
  fatigaMaximo?: number;
  onIrAPaso: (p: PasoAsistente) => void;
  /** El mapeo guardado: con él se sabe a qué variables sin resolver les falta una decisión. Sin él, se muestran todas. */
  mapeo?: MapeoVariables;
  /** Para decir «Nombre» en vez de «primerNombre». */
  campos?: CampoDisponible[];
  /** true si el operador eligió una fecha; false = «Ahora». */
  programado?: boolean;
}

export function ResumenChequeo({ chequeo, fatigaDias, fatigaMaximo = 1, onIrAPaso, mapeo, campos = [], programado = false }: Props) {
  const filas = filasExclusion(chequeo, fatigaDias, fatigaMaximo);
  const afuera = Math.max(0, (Number(chequeo.total) || 0) - (Number(chequeo.incluidos) || 0));
  const faltan = mapeo ? sinDecision(chequeo, mapeo) : (chequeo.variablesSinResolver || []).filter((v) => v.contactos > 0);
  const bloqueos = chequeo.bloqueos || [];
  const advertencias = chequeo.advertencias || [];
  const etiqueta = (campo: string) => campos.find((c) => c.campo === campo)?.etiqueta || campo;

  return (
    <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm space-y-5" aria-label="Resumen del chequeo">
      <div>
        <p className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
          <strong data-testid="envio">{num(chequeo.envio)}</strong>{" "}
          {plural(chequeo.envio, "persona recibe el mensaje", "personas reciben el mensaje")}
        </p>
        {/* Sin grupo de control: los que pasan el chequeo reciben todos; sólo se aparta a los excluidos. */}
        <p className="text-sm text-slate-500 mt-1">
          {num(afuera)} {plural(afuera, "queda", "quedan")} afuera
        </p>
        {/* Con el límite de Meta, los que menos bloquean van primero (el orden de la cola sale del chequeo). */}
        {textoCalientes(chequeo.calientes) && <p className="text-sm text-slate-500 mt-1" data-testid="calientes">{textoCalientes(chequeo.calientes)}</p>}
      </div>

      <table className="w-full text-sm">
        <caption className="sr-only">Cómo se llega a los que reciben el mensaje</caption>
        <tbody className="divide-y divide-slate-100">
          <tr>
            <th scope="row" className="py-2 text-left font-medium text-slate-700">
              <span className="inline-flex items-center gap-2"><Users size={14} className="text-slate-400" /> Total en la audiencia</span>
            </th>
            <td className="py-2 text-right font-semibold text-slate-900 tabular-nums">{num(chequeo.total)}</td>
          </tr>
          {filas.map((f) => (
            <tr key={f.motivo} data-testid={`exclusion-${f.motivo}`}>
              <th scope="row" className="py-2 pl-6 text-left font-normal">
                <span className="block text-slate-700">− {f.titulo}</span>
                <span className="block text-xs text-slate-400">{f.detalle}</span>
              </th>
              <td className="py-2 text-right text-slate-600 tabular-nums align-top">−{num(f.cantidad)}</td>
            </tr>
          ))}
          <tr>
            <th scope="row" className="py-2 text-left font-bold text-slate-900">Reciben</th>
            <td className="py-2 text-right font-bold text-violet-700 tabular-nums">{num(chequeo.envio)}</td>
          </tr>
        </tbody>
      </table>

      {faltan.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4" role="alert">
          <p className="text-sm font-semibold text-red-700">Faltan datos para completar el mensaje</p>
          <ul className="mt-1.5 space-y-1 text-sm text-red-700">
            {faltan.map((v) => (
              <li key={v.variable}>
                A {num(v.contactos)} {plural(v.contactos, "contacto le falta", "contactos les falta")} «{etiqueta(v.campo)}».
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => onIrAPaso("mensaje")}
            className="mt-3 h-11 px-3 rounded-lg bg-white border border-red-200 text-sm font-semibold text-red-700 hover:bg-red-100"
          >
            Ir a Variables
          </button>
        </div>
      )}

      {bloqueos.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4" role="alert">
          <p className="flex items-center gap-2 text-sm font-semibold text-red-700">
            <OctagonAlert size={15} /> No se puede enviar todavía
          </p>
          <ul className="mt-2 space-y-2">
            {bloqueos.map((code) => (
              <FilaBloqueo
                key={code}
                code={code}
                faltanteUSD={chequeo.saldo?.faltanteUSD}
                comprometidoUSD={chequeo.saldo?.comprometidoUSD}
                gruposEnOtraCampana={chequeo.gruposEnOtraCampana}
                onIrAPaso={onIrAPaso}
                // El bloque de arriba ya ofrece «Ir a Variables»: no se repite el mismo botón dos veces.
                sinAccion={code === "VARIABLES_SIN_RESOLVER" && faltan.length > 0}
              />
            ))}
          </ul>
        </div>
      )}

      {advertencias.length > 0 && (
        <ul className="space-y-1.5">
          {advertencias.map((a, i) => (
            <li key={`${a.codigo}-${i}`} className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              {a.mensaje}
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-start gap-2 text-sm text-slate-600">
        <Clock size={14} className="mt-0.5 text-slate-400 flex-shrink-0" />
        <p>
          Franja: {chequeo.franjas || "—"} · Ahora está <strong className={chequeo.enFranjaAhora ? "text-emerald-700" : "text-amber-700"}>{chequeo.enFranjaAhora ? "abierta" : "cerrada"}</strong>.
          {!programado && !chequeo.enFranjaAhora && <> Si confirmás ahora, empieza cuando se abra la franja.</>}
        </p>
      </div>
    </section>
  );
}

function FilaBloqueo({ code, faltanteUSD, comprometidoUSD, gruposEnOtraCampana, onIrAPaso, sinAccion }: {
  code: CodigoBloqueo; faltanteUSD?: number; comprometidoUSD?: number; gruposEnOtraCampana?: string; onIrAPaso: (p: PasoAsistente) => void; sinAccion: boolean;
}) {
  const b = BLOQUEO[code];
  const accion = sinAccion ? undefined : b?.accion;
  return (
    <li className="flex items-start justify-between gap-3 flex-wrap" data-testid={`bloqueo-${code}`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-red-700">{tituloBloqueo(code, { faltanteUSD, comprometidoUSD, gruposEnOtraCampana })}</p>
        {b?.detalle && <p className="text-xs text-red-600/80">{b.detalle}</p>}
      </div>
      {accion?.paso && (
        <button
          type="button"
          onClick={() => onIrAPaso(accion.paso as PasoAsistente)}
          className="h-11 px-3 rounded-lg bg-white border border-red-200 text-xs font-semibold text-red-700 hover:bg-red-100"
        >
          {accion.label}
        </button>
      )}
      {!accion?.paso && accion?.ruta && (
        <Link to={accion.ruta} className="h-11 px-3 rounded-lg bg-white border border-red-200 text-xs font-semibold text-red-700 hover:bg-red-100 inline-flex items-center">
          {accion.label}
        </Link>
      )}
    </li>
  );
}

/**
 * A cuántos les llega y, en el mismo lugar, por qué queda afuera el resto (paso Variables). Antes decía «158 quedan
 * afuera (el detalle está en Confirmar)»: el operador veía que dos de cada tres no recibían y tenía que avanzar dos
 * pasos a ciegas para saber si era un error suyo. Mismos números y textos que el Resumen de Confirmar.
 */
export function QuienesQuedanAfuera({ chequeo, fatigaDias, fatigaMaximo = 1 }: { chequeo: Chequeo; fatigaDias: number; fatigaMaximo?: number }) {
  const total = Number(chequeo.total) || 0;
  const incluidos = Number(chequeo.incluidos) || 0;
  const afuera = Math.max(0, total - incluidos);
  const filas = filasExclusion(chequeo, fatigaDias, fatigaMaximo);
  return (
    <div className="text-sm text-slate-700">
      <p>
        <strong>{num(incluidos)}</strong> de {num(total)} reciben
        {afuera > 0 && <> · {num(afuera)} {plural(afuera, "queda", "quedan")} afuera{filas.length > 0 ? ":" : ""}</>}
      </p>
      {filas.length > 0 && (
        <ul aria-label="Por qué quedan afuera" className="mt-2 divide-y divide-slate-100 rounded-lg bg-slate-50 px-3">
          {filas.map((f) => (
            <li key={f.motivo} data-testid={`afuera-${f.motivo}`} className="flex items-start justify-between gap-3 py-1.5 text-xs">
              <span>
                <span className="font-medium text-slate-700">{f.titulo}</span>
                <span className="block text-slate-500">{f.detalle}</span>
              </span>
              <span className="tabular-nums font-semibold text-slate-700">{num(f.cantidad)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default ResumenChequeo;
